import { useEffect, useState } from "react";
import apiClient from "@/services/apiClient";

const statusStyles = {
  Paid: "bg-emerald-50 text-emerald-700",
  Pending: "bg-amber-50 text-amber-700",
  Failed: "bg-rose-50 text-rose-700",
  Refunded: "bg-blue-50 text-blue-700",
};

const formatAmount = (amount) => new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
}).format(Number(amount || 0)) + " AUD";

const formatDate = (date) => date
  ? new Intl.DateTimeFormat("en-AU", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(date))
  : "-";

export default function StudentPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const response = await apiClient.get(`${apiBaseUrl}/StudentBooking/MyBookings`);
        const bookings = response.data?.bookings || [];
        setPayments(bookings.filter((booking) => booking.paymentTransactionId));
      } catch (requestError) {
        console.error("Failed to load student payments", requestError);
        setError(requestError.response?.data?.message || "Unable to load your payment history.");
      } finally {
        setLoading(false);
      }
    };

    loadPayments();
  }, [apiBaseUrl]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Payment history</h1>
      <div className="wc-card p-0 overflow-hidden">
        {loading && <div className="px-6 py-12 text-center text-sm text-slate-500">Loading payment history...</div>}
        {!loading && error && <div className="px-6 py-4 text-sm text-rose-700 bg-rose-50">{error}</div>}
        {!loading && !error && payments.length === 0 && <div className="px-6 py-12 text-center text-sm text-slate-500">No payments found.</div>}
        {!loading && !error && payments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Payment Reference</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Course</th>
                  <th className="text-left px-4 py-3">Method</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((payment) => (
                  <tr key={payment.paymentTransactionId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold">{payment.receiptNumber || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(payment.paidDate)}</td>
                    <td className="px-4 py-3"><div className="font-medium text-[#0A2540]">{payment.courseName}</div><div className="text-xs text-slate-500">{payment.bookingNumber}</div></td>
                    <td className="px-4 py-3 text-slate-600">{payment.paymentMethod || "-"}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatAmount(payment.totalAmount)}</td>
                    <td className="px-4 py-3"><span className={`wc-badge ${statusStyles[payment.paymentStatus] || "bg-slate-100 text-slate-700"}`}>{payment.paymentStatus || "Unknown"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
